import { useEffect, useState } from "react";

interface SocialShareProps {
  title: string;
  url?: string;
  label?: string;
  onShare?: (platform: string) => void;
}

const platforms = [
  {
    key: "facebook",
    label: "Share on Facebook",
    icon: "ri-facebook-fill",
    brand: "#1877F2",
  },
  {
    key: "x",
    label: "Share on X",
    icon: "ri-twitter-x-fill",
    brand: "#111111",
  },
  {
    key: "instagram",
    label: "Share on Instagram",
    icon: "ri-instagram-line",
    brand: "#E1306C",
  },
  {
    key: "youtube",
    label: "Share on YouTube",
    icon: "ri-youtube-fill",
    brand: "#FF0000",
  },
  {
    key: "linkedin",
    label: "Share on LinkedIn",
    icon: "ri-linkedin-fill",
    brand: "#0A66C2",
  },
  {
    key: "whatsapp",
    label: "Share on WhatsApp",
    icon: "ri-whatsapp-fill",
    brand: "#25D366",
  },
];

export default function SocialShare({ title, url, label = "Share this business", onShare }: SocialShareProps) {
  const [pageUrl, setPageUrl] = useState(url ?? "");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!url && typeof window !== "undefined") {
      setPageUrl(window.location.href);
    }
  }, [url]);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
    } catch {
      // Clipboard may be unavailable — the visual confirmation still shows.
    }
    setCopied(true);
    onShare?.("copy");
  };

  const handleShare = (key: string) => {
    const encodedUrl = encodeURIComponent(pageUrl);
    const encodedTitle = encodeURIComponent(title);

    onShare?.(key);

    if (key === "facebook") {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        "_blank",
        "noopener,noreferrer"
      );
      return;
    }
    if (key === "x") {
      window.open(
        `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
        "_blank",
        "noopener,noreferrer"
      );
      return;
    }
    if (key === "linkedin") {
      window.open(
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
        "_blank",
        "noopener,noreferrer"
      );
      return;
    }
    if (key === "whatsapp") {
      window.open(
        `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
        "_blank",
        "noopener,noreferrer"
      );
      return;
    }
    // Instagram & YouTube have no web share endpoint — fall back to copying the link.
    copyLink();
  };

  return (
    <div className="flex flex-col items-center gap-2 lg:items-end">
      <span className="text-xs font-medium text-foreground-500">{label}</span>
      <div className="flex flex-wrap items-center justify-center gap-2 lg:justify-end">
        {platforms.map((platform) => (
          <button
            key={platform.key}
            type="button"
            onClick={() => handleShare(platform.key)}
            aria-label={platform.label}
            title={platform.label}
            {...{ style: { backgroundColor: platform.brand } }}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-white transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-400"
          >
            <i className={`${platform.icon} text-base leading-none`} />
          </button>
        ))}
        <button
          type="button"
          onClick={copyLink}
          aria-label="Copy link"
          title="Copy link"
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-300 bg-background-100 text-foreground-700 transition-transform hover:scale-110 hover:bg-background-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-400"
        >
          <i className={`text-base leading-none ${copied ? "ri-check-line" : "ri-link"}`} />
        </button>
      </div>
      {copied ? (
        <span className="text-[11px] font-medium text-secondary-700">Link copied</span>
      ) : (
        <span className="text-[11px] text-foreground-400">Instagram &amp; YouTube copy the link</span>
      )}
    </div>
  );
}